import { useFocusTrap } from "@hooks/useFocusTrap"
import { cn } from "@lib/cn"
import { X } from "lucide-react"
import { useEffect, useId, useRef, type ReactNode } from "react"

type Size = "sm" | "md" | "lg" | "xl"
type MobilePlacement = "bottom" | "center" | "fullscreen"

const sizeClasses: Record<Size, string> = {
  sm: "md:max-w-sm",
  md: "md:max-w-md",
  lg: "md:max-w-lg",
  // A table's worth of width — a panel you work in, not a question you answer.
  xl: "md:max-w-4xl",
}

// Mobile shell per placement. dvh, not vh: iOS counts the collapsing URL bar
// in vh, so a 90vh sheet ran under it (`responsive-design.md` §18).
const mobilePanelClasses: Record<MobilePlacement, string> = {
  bottom: "max-h-[90dvh] rounded-t-2xl",
  center: "max-h-[90dvh] rounded-2xl",
  // `responsive-design.md` §15: a surface with a list to work through takes
  // the whole phone; a sheet would leave a sliver of page nobody can use.
  fullscreen: "h-[100dvh] max-h-[100dvh] md:h-auto",
}

const mobileContainerClasses: Record<MobilePlacement, string> = {
  bottom: "items-end md:items-center",
  center: "items-center p-4",
  fullscreen: "items-stretch md:items-center",
}

interface Props {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  size?: Size
  footer?: ReactNode
  children: ReactNode
  /** Optional classes for the modal panel shell. */
  panelClassName?: string
  /** Optional classes for the scrollable modal body. */
  bodyClassName?: string
  /** Optional override for the close-button aria-label (defaults to "Cerrar"). */
  closeLabel?: string
  /** Mobile placement. Defaults to bottom sheet; `center` for dialog-style
   *  mobile modals; `fullscreen` for a panel the professor works through. */
  mobilePlacement?: MobilePlacement
  /** When true, scrim click does NOT close the modal. Defaults to false. */
  blockScrimClose?: boolean
  /** False = only the dialog's own buttons close it: no X, no Escape, no
   *  scrim. For an outcome the professor must not dismiss by accident (a
   *  student's new link shown once, PRD-73). Defaults to true. */
  dismissible?: boolean
  /** Let a long title wrap instead of truncating — when the title names
   *  something the reader must not lose half of. Defaults to false. */
  titleWraps?: boolean
}

/** Generic modal — centered card on desktop, bottom-sheet on mobile by default.
 *
 * Used for focused tasks that demand the user's full attention: edit (PRD-09
 * §FR-22), add (PRD-04 revised 2026-05-03 §FR-17–18), confirmation flows.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = "md",
  footer,
  children,
  panelClassName,
  bodyClassName,
  closeLabel = "Cerrar",
  mobilePlacement = "bottom",
  blockScrimClose,
  dismissible = true,
  titleWraps = false,
}: Props) {
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const titleId = useId()
  const descId = useId()

  // Keep the latest onClose in a ref so the open/close effect below can read
  // it without re-firing every time the parent passes a new function
  // reference. Without this, every parent re-render would tear down + rebuild
  // the focus trap and restore focus to the trigger element — which kicks the
  // user out of whatever input they're typing in.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])
  // Same reasoning: Escape reads the latest value without re-arming the trap.
  const dismissibleRef = useRef(dismissible)
  useEffect(() => {
    dismissibleRef.current = dismissible
  }, [dismissible])

  // Initial focus, the Tab cycle and focus return (shared with the preview
  // drawer through `useFocusTrap`).
  useFocusTrap(dialogRef, open)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissibleRef.current) onCloseRef.current()
    }
    document.addEventListener("keydown", onKey)
    // Lock background scroll while open.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = previousOverflow
    }
    // Intentionally only depends on `open` — onClose is read via ref so the
    // effect doesn't tear down on parent re-renders.
  }, [open])

  if (!open) return null

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex justify-center",
        mobileContainerClasses[mobilePlacement],
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={blockScrimClose || !dismissible ? undefined : onClose}
        className="bg-overlay absolute inset-0 backdrop-blur-sm"
      />
      <div
        ref={dialogRef}
        className={cn(
          "bg-surface relative flex w-full flex-col overflow-hidden shadow-2xl",
          mobilePanelClasses[mobilePlacement],
          // Desktop: centered card.
          "md:max-h-[85dvh] md:w-full md:rounded-2xl",
          sizeClasses[size],
          // Animations — honored only when motion isn't reduced.
          "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-150",
          panelClassName,
        )}
      >
        <header className="border-border flex items-start justify-between gap-3 border-b px-5 py-4">
          <div className="min-w-0">
            <h2
              id={titleId}
              className={cn(
                "text-ink text-base font-semibold",
                titleWraps ? "break-words" : "truncate",
              )}
            >
              {title}
            </h2>
            {description && (
              <p id={descId} className="text-ink-subtle mt-0.5 text-xs">
                {description}
              </p>
            )}
          </div>
          {dismissible ? (
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="rounded-control text-ink-subtle hover:bg-inset inline-flex h-11 w-11 flex-shrink-0 items-center justify-center focus:ring-2 focus:outline-none md:h-8 md:w-8"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </header>
        <div className={cn("flex-1 overflow-y-auto px-5 py-4", bodyClassName)}>{children}</div>
        {footer && <footer className="border-border border-t px-5 py-3">{footer}</footer>}
      </div>
    </div>
  )
}
